import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, Lesson, Content, ContentFormData } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import {
  ArrowLeft,
  Save,
  FileText,
  Layers,
  Edit,
  Code,
  Type,
  Eye,
  Volume2,
  CheckCircle
} from 'lucide-react'
import CodeEditor from '@/components/common/CodeEditor'
import { sanitizeHtml } from '@/lib/sanitize'

export default function ContentForm() {
  const { contentId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditing = Boolean(contentId) && contentId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  // Preserve filter params for navigation back to list
  const filterQuery = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const listUrl = `/admin/contents${filterQuery}`

  const [units, setUnits] = useState<Unit[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [selectedUnit, setSelectedUnit] = useState<string>('')
  const [formData, setFormData] = useState<ContentFormData>({
    lesson_id: '',
    title: '',
    theory_content: '',
    video_url: '',
    audio_url: '',
    order_num: 1,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [editorMode, setEditorMode] = useState<'text' | 'code'>('text')
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    loadUnits()
    if (isEditing) {
      loadContent()
    }
  }, [contentId])

  const loadUnits = async () => {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('order_num', { ascending: true })

      if (error) throw error
      setUnits(data || [])
    } catch (error) {
      console.error('Error loading units:', error)
    }
  }

  const loadLessons = async (unitId: string) => {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('unit_id', unitId)
        .order('order_num', { ascending: true })

      if (error) throw error
      const lessonsData = data as unknown as Lesson[]
      setLessons(lessonsData || [])

      // Si no estamos editando y hay lecciones, seleccionar la primera
      if (!isEditing && lessonsData && lessonsData.length > 0 && !formData.lesson_id) {
        setFormData(prev => ({ ...prev, lesson_id: lessonsData[0].id }))
        loadNextOrderNum(lessonsData[0].id)
      }
    } catch (error) {
      console.error('Error loading lessons:', error)
    }
  }

  const loadContent = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('contents')
        .select(`
          *,
          lesson:lessons(*, unit:units(*))
        `)
        .eq('id', contentId as string)
        .single()

      if (error) throw error

      const contentData = data as unknown as Content & { lesson: Lesson & { unit: Unit } }

      setSelectedUnit(contentData.lesson?.unit?.id || '')
      await loadLessons(contentData.lesson?.unit?.id || '')

      setFormData({
        lesson_id: contentData.lesson_id,
        title: contentData.title,
        theory_content: contentData.theory_content || '',
        video_url: contentData.video_url || '',
        audio_url: contentData.audio_url || '',
        order_num: contentData.order_num,
        is_active: contentData.is_active
      })
    } catch (error) {
      console.error('Error loading content:', error)
      setError('Error al cargar el contenido')
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextOrderNum = async (lessonId: string) => {
    try {
      const { data } = await supabase
        .from('contents')
        .select('order_num')
        .eq('lesson_id', lessonId)
        .order('order_num', { ascending: false })
        .limit(1)

      const orderData = data as unknown as { order_num: number }[]
      if (orderData && orderData.length > 0) {
        setFormData(prev => ({ ...prev, order_num: orderData[0].order_num + 1 }))
      } else {
        setFormData(prev => ({ ...prev, order_num: 1 }))
      }
    } catch (error) {
      console.error('Error loading order num:', error)
    }
  }

  const handleUnitChange = (unitId: string) => {
    setSelectedUnit(unitId)
    setFormData(prev => ({ ...prev, lesson_id: '' }))
    setLessons([])
    if (unitId) {
      loadLessons(unitId)
    }
  }

  const handleLessonChange = (lessonId: string) => {
    setFormData(prev => ({ ...prev, lesson_id: lessonId }))
    if (!isEditing) {
      loadNextOrderNum(lessonId)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      const contentData = {
        lesson_id: formData.lesson_id,
        title: formData.title,
        theory_content: formData.theory_content || null,
        video_url: formData.video_url || null,
        audio_url: formData.audio_url || null,
        order_num: formData.order_num,
        is_active: formData.is_active
      }

      if (isEditing) {
        const { error } = await (supabase
          .from('contents') as any)
          .update({
            ...contentData,
            updated_at: new Date().toISOString()
          })
          .eq('id', contentId as string)

        if (error) throw error
        // Go to preview of the content just saved
        navigate(`/admin/contents/${contentId}${filterQuery}`)
      } else {
        const { data: newContent, error } = await (supabase
          .from('contents') as any)
          .insert(contentData)
          .select('id')
          .single()

        if (error) throw error
        // Go to preview of the newly created content
        navigate(`/admin/contents/${newContent.id}${filterQuery}`)
      }
    } catch (error: any) {
      console.error('Error saving content:', error)
      setError(error.message || 'Error al guardar el contenido')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className={`mx-auto space-y-6 ${isViewMode ? 'max-w-5xl' : 'max-w-3xl'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={listUrl}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Contenido' : (isEditing ? 'Editar Contenido' : 'Nuevo Contenido')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa del contenido' : (isEditing ? 'Modifica los datos del contenido' : 'Crea un nuevo contenido')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/contents/${contentId}/edit${filterQuery}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Edit className="w-5 h-5" />
              Editar
            </Link>
            <Link
              to={listUrl}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              OK
            </Link>
          </div>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Unit & Lesson Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <Layers className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Ubicación</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="content-unit" className="block text-sm font-medium text-gray-700 mb-1">
                Unidad
              </label>
              <select id="content-unit" value={selectedUnit} 
                onChange={(e) => handleUnitChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar unidad...</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    Unidad {unit.order_num}: {unit.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="content-lesson" className="block text-sm font-medium text-gray-700 mb-1">
                Lección
              </label>
              <select id="content-lesson" value={formData.lesson_id}
                onChange={(e) => handleLessonChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={!selectedUnit || isViewMode}
              >
                <option value="">Seleccionar lección...</option>
                {lessons.map(lesson => (
                  <option key={lesson.id} value={lesson.id}>
                    Lección {lesson.order_num}: {lesson.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content Section */}
        {isViewMode ? (
          /* ====== VISTA PREVIA REAL (como la ve el alumno) ====== */
          <div className="space-y-6">
            {/* Info bar */}
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="px-2 py-1 bg-gray-100 rounded">Contenido #{formData.order_num}</span>
              <span className="font-medium text-gray-900 text-lg">{formData.title}</span>
              {!formData.is_active && (
                <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs font-medium">Inactivo</span>
              )}
            </div>

            {/* Simulacion vista alumno */}
            <div className="border-2 border-dashed border-[#0055A4]/30 rounded-2xl p-1">
              <div className="bg-[#0055A4]/5 px-4 py-2 rounded-t-xl flex items-center gap-2 text-sm text-[#0055A4] font-medium">
                <Eye className="w-4 h-4" />
                Vista previa del alumno
              </div>
              <div className="bg-gray-50 rounded-b-xl p-4 md:p-6 space-y-6">
                {/* Video embed */}
                {formData.video_url && (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                      {formData.video_url.includes('youtube') || formData.video_url.includes('youtu.be') ? (
                        <iframe
                          src={formData.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                          title="Video del contenido"
                          className="w-full h-full"
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      ) : (
                        <video src={formData.video_url} controls className="w-full h-full">
                          <track kind="captions" srcLang="es" label="Español" />
                        </video>
                      )}
                    </div>
                  </div>
                )}

                {/* Audio player */}
                {formData.audio_url && (
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-[#0055A4]/10">
                        <Volume2 className="w-5 h-5 text-[#0055A4]" />
                      </div>
                      <span className="font-medium text-gray-900">Audio de la lección</span>
                    </div>
                    <AudioPlayer src={formData.audio_url} className="w-full" />
                  </div>
                )}

                {/* Theory content con estilos identicos al alumno */}
                {formData.theory_content ? (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div
                      className="theory-content prose prose-lg max-w-none
                        prose-headings:text-gray-900 prose-headings:font-bold
                        prose-h1:text-3xl prose-h1:text-[#0055A4] prose-h1:border-b prose-h1:border-[#0055A4]/20 prose-h1:pb-2 prose-h1:mb-4
                        prose-h2:text-2xl prose-h2:text-[#0055A4] prose-h2:mt-6 prose-h2:mb-3
                        prose-h3:text-xl prose-h3:text-gray-800 prose-h3:mt-4 prose-h3:mb-2
                        prose-p:text-gray-700 prose-p:leading-relaxed prose-p:my-3
                        prose-a:text-[#0055A4] prose-a:underline hover:prose-a:text-[#003d7a]
                        prose-strong:text-gray-900 prose-strong:font-semibold
                        prose-em:italic prose-em:text-gray-600
                        prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                        prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                        prose-li:text-gray-700 prose-li:my-1
                        prose-blockquote:border-l-4 prose-blockquote:border-[#0055A4]/40 prose-blockquote:bg-[#0055A4]/5 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:my-4 prose-blockquote:italic prose-blockquote:text-gray-600
                        prose-table:w-full prose-table:my-4 prose-table:border-collapse
                        prose-th:bg-[#0055A4]/10 prose-th:text-[#003d7a] prose-th:font-semibold prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-[#0055A4]/20
                        prose-td:p-3 prose-td:border prose-td:border-gray-200
                        prose-tr:even:bg-gray-50
                        prose-img:rounded-lg prose-img:shadow-md prose-img:my-4
                        prose-code:bg-gray-100 prose-code:text-pink-600 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                        [&_.vocab-box]:bg-gradient-to-r [&_.vocab-box]:from-blue-50 [&_.vocab-box]:to-indigo-50 [&_.vocab-box]:p-4 [&_.vocab-box]:rounded-xl [&_.vocab-box]:border [&_.vocab-box]:border-[#0055A4]/20 [&_.vocab-box]:my-4
                        [&_.example-box]:bg-green-50 [&_.example-box]:p-4 [&_.example-box]:rounded-xl [&_.example-box]:border-l-4 [&_.example-box]:border-green-400 [&_.example-box]:my-4
                        [&_.tip-box]:bg-yellow-50 [&_.tip-box]:p-4 [&_.tip-box]:rounded-xl [&_.tip-box]:border-l-4 [&_.tip-box]:border-yellow-400 [&_.tip-box]:my-4
                        [&_.warning-box]:bg-red-50 [&_.warning-box]:p-4 [&_.warning-box]:rounded-xl [&_.warning-box]:border-l-4 [&_.warning-box]:border-red-400 [&_.warning-box]:my-4
                        [&_.french]:text-[#0055A4] [&_.french]:font-medium
                        [&_.spanish]:text-gray-500 [&_.spanish]:italic
                        [&_.pronunciation]:text-purple-600 [&_.pronunciation]:text-sm
                        [&_.dialogue]:bg-gray-50 [&_.dialogue]:p-4 [&_.dialogue]:rounded-xl [&_.dialogue]:my-4 [&_.dialogue]:font-mono
                        [&_.speaker-a]:text-[#0055A4] [&_.speaker-a]:font-semibold
                        [&_.speaker-b]:text-green-600 [&_.speaker-b]:font-semibold
                      "
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.theory_content.replaceAll(/<audio\b/gi, '<audio controlsList="nodownload" oncontextmenu="return false"')) }}
                    />
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-400">Sin contenido teorico</p>
                  </div>
                )}

                {/* Sin contenido multimedia ni teorico */}
                {!formData.video_url && !formData.audio_url && !formData.theory_content && (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Este contenido no tiene material visible para el alumno</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ====== MODO EDICION ====== */
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-orange-100">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Información del Contenido</h2>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="content-order" className="block text-sm font-medium text-gray-700 mb-1">
                  Orden
                </label>
                <input id="content-order" type="number" 
                  min="1"
                  value={formData.order_num}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_num: Number.parseInt(e.target.value) || 1}))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                  required
                />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="content-title"className="block text-sm font-medium text-gray-700 mb-1">
                  Título del Contenido
                </label>
                <input id="content-title" type="text" value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: Los articulos definidos"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="content-theory" className="block text-sm font-medium text-gray-700">
                  Contenido Teorico
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setEditorMode('text')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                        editorMode === 'text'
                          ? 'bg-[#0055A4] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Type className="w-4 h-4" />
                      Texto libre
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode('code')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                        editorMode === 'code'
                          ? 'bg-[#0055A4] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Code className="w-4 h-4" />
                      Código HTML
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      showPreview
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Vista previa
                  </button>
                </div>
              </div>

              {showPreview ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Editor</p>
                    {editorMode === 'code' ? (
                      <CodeEditor
                        value={formData.theory_content}
                        onChange={(value) => setFormData(prev => ({ ...prev, theory_content: value }))}
                        placeholder="Escribe HTML aquí..."
                        language="html"
                      />
                    ) : (
                      <textarea id="content-theory" value={formData.theory_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, theory_content: e.target.value }))}
                        placeholder="Escribe el contenido teorico..."
                        rows={10}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] font-mono text-sm"
                      />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Vista previa (como la ve el alumno)</p>
                    <div className="theory-content w-full border rounded-lg bg-white min-h-[300px] overflow-auto p-6
                      prose prose-lg max-w-none
                      prose-headings:text-gray-900 prose-headings:font-bold
                      prose-h1:text-3xl prose-h1:text-[#0055A4] prose-h1:border-b prose-h1:border-[#0055A4]/20 prose-h1:pb-2 prose-h1:mb-4
                      prose-h2:text-2xl prose-h2:text-[#0055A4] prose-h2:mt-6 prose-h2:mb-3
                      prose-h3:text-xl prose-h3:text-gray-800 prose-h3:mt-4 prose-h3:mb-2
                      prose-p:text-gray-700 prose-p:leading-relaxed prose-p:my-3
                      prose-a:text-[#0055A4] prose-a:underline hover:prose-a:text-[#003d7a]
                      prose-strong:text-gray-900 prose-strong:font-semibold
                      prose-em:italic prose-em:text-gray-600
                      prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                      prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                      prose-li:text-gray-700 prose-li:my-1
                      prose-blockquote:border-l-4 prose-blockquote:border-[#0055A4]/40 prose-blockquote:bg-[#0055A4]/5 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:my-4 prose-blockquote:italic prose-blockquote:text-gray-600
                      prose-table:w-full prose-table:my-4 prose-table:border-collapse
                      prose-th:bg-[#0055A4]/10 prose-th:text-[#003d7a] prose-th:font-semibold prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-[#0055A4]/20
                      prose-td:p-3 prose-td:border prose-td:border-gray-200
                      prose-tr:even:bg-gray-50
                      prose-img:rounded-lg prose-img:shadow-md prose-img:my-4
                      prose-code:bg-gray-100 prose-code:text-pink-600 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                      prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                      [&_.vocab-box]:bg-gradient-to-r [&_.vocab-box]:from-blue-50 [&_.vocab-box]:to-indigo-50 [&_.vocab-box]:p-4 [&_.vocab-box]:rounded-xl [&_.vocab-box]:border [&_.vocab-box]:border-[#0055A4]/20 [&_.vocab-box]:my-4
                      [&_.example-box]:bg-green-50 [&_.example-box]:p-4 [&_.example-box]:rounded-xl [&_.example-box]:border-l-4 [&_.example-box]:border-green-400 [&_.example-box]:my-4
                      [&_.tip-box]:bg-yellow-50 [&_.tip-box]:p-4 [&_.tip-box]:rounded-xl [&_.tip-box]:border-l-4 [&_.tip-box]:border-yellow-400 [&_.tip-box]:my-4
                      [&_.warning-box]:bg-red-50 [&_.warning-box]:p-4 [&_.warning-box]:rounded-xl [&_.warning-box]:border-l-4 [&_.warning-box]:border-red-400 [&_.warning-box]:my-4
                      [&_.french]:text-[#0055A4] [&_.french]:font-medium
                      [&_.spanish]:text-gray-500 [&_.spanish]:italic
                      [&_.pronunciation]:text-purple-600 [&_.pronunciation]:text-sm
                      [&_.dialogue]:bg-gray-50 [&_.dialogue]:p-4 [&_.dialogue]:rounded-xl [&_.dialogue]:my-4 [&_.dialogue]:font-mono
                      [&_.speaker-a]:text-[#0055A4] [&_.speaker-a]:font-semibold
                      [&_.speaker-b]:text-green-600 [&_.speaker-b]:font-semibold
                    ">
                      {formData.theory_content ? (
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.theory_content.replace(/<audio\b/gi, '<audio controlsList="nodownload" oncontextmenu="return false"')) }} />
                      ) : (
                        <p className="text-gray-400">Escribe para ver la vista previa...</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : editorMode === 'code' ? (
                <CodeEditor
                  value={formData.theory_content}
                  onChange={(value) => setFormData(prev => ({ ...prev, theory_content: value }))}
                  placeholder="Escribe HTML aquí..."
                  language="html"
                />
              ) : (
                <textarea id="content-theory" value={formData.theory_content}
                  onChange={(e) => setFormData(prev => ({ ...prev, theory_content: e.target.value }))}
                  placeholder="Escribe el contenido teorico..."
                  rows={10}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] font-mono text-sm"
                />
              )}

              <p className="text-xs text-gray-500 mt-1">
                {editorMode === 'code'
                  ? "Escribe código HTML directamente. Puedes usar etiquetas como <h1>, <p>, <strong>, <ul>, <table>, etc."
                  : "Puedes usar Markdown: **negrita**, *italica*, # encabezados, listas, etc."
                }
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="content-video-url" className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Video (opcional)
                </label>
                <input id="content-video-url" type="url" value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                />
              </div>
              <div>
                <label htmlFor="content-audio-url"  className="block text-sm font-medium text-gray-700 mb-1">
                  URL de Audio (opcional)
                </label>
                <input id="content-audio-url" type="url" value={formData.audio_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, audio_url: e.target.value }))}
                  placeholder="https://example.com/audio.mp3"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4]"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Contenido activo (visible para estudiantes)
              </label>
            </div>
          </div>
        )}

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to={listUrl}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.lesson_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Contenido')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
